/**
 * Related Products Tracking Utilities
 *
 * This file has functions to add analytics tracking parameters
 * to related product links on Optiversal buy pages.
 */

/**
 * Appends the icid2 tracking parameter to a URL
 * https://jira.sephora.com/browse/ECS-8859
 *
 * @param {string} url - The URL to modify
 * @param {string} trackingParam - The tracking parameter value (default: 'seop_relatedlinks')
 * @returns {string} The URL with the tracking parameter appended
 */
export function appendTrackingParam(url, trackingParam = 'seop_relatedlinks') {
    if (!url) {
        return url;
    }

    try {
        const urlObj = new URL(url, window.location.origin);

        urlObj.searchParams.set('icid2', trackingParam);

        return urlObj.pathname + urlObj.search;
    } catch (error) {
        // Fallback for invalid URLs or server-side context
        const separator = url.includes('?') ? '&' : '?';

        return `${url}${separator}icid2=${trackingParam}`;
    }
}

/**
 * Adds tracking parameters to all related product links on the page
 *
 * This function finds all related product links
 * and appends the icid2 tracking parameter to their href attributes.
 *
 * @param {string} containerSelector - Optional container selector to limit scope (default: document)
 * @param {string} trackingParam - The tracking parameter value (default: 'seop_relatedlinks')
 */
export function addRelatedProductTracking(containerSelector = null, trackingParam = 'seop_relatedlinks') {
    // SSR safety check
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    try {
        // Find the container or use document
        const container = containerSelector ? document.querySelector(containerSelector) : document;

        if (!container) {
            return;
        }

        // Find all related product links (Based on Optiversal feedback these won't change)
        const relatedProductLinks = container.querySelectorAll('.opt-related-product');

        // Add tracking parameter to each link
        relatedProductLinks.forEach(link => {
            const currentHref = link.getAttribute('href');

            if (currentHref) {
                const updatedHref = appendTrackingParam(currentHref, trackingParam);

                link.setAttribute('href', updatedHref);
            }
        });
    } catch (error) {
        Sephora.logger.error('Error adding related product tracking:', error);
    }
}
